import DebounceEventListener from '../../Assets/helpers/events/debounce-listener.js';
import ThrottleEventListener from '../../Assets/helpers/events/throttle-listener.js';
import { isMobile } from '../../Assets/configuration/index.js';

class Carousel extends HTMLElement {
  constructor() {
    super();

    this.options = mergeOptionsFromAttributes(this, {
      autoPlay: false,
      autoPlaySpeed: 5,
      loop: false,
      observeSizeChangeItems: true,
      marginForDisplayNavButton: 50,
      slideControls: false,
      navButtons: true,
      customizeArrow: false,
      disableOnMobile: false
    });

    // Disable carousel on mobile for web performance
    if (this.options.disableOnMobile && isMobile) {
      return;
    }

    this.els = {
      scroller: this.querySelector('.f-carousel__scroller'),
      items: this.querySelectorAll('.f-carousel__scroller > *')
    };

    this.index = {
      first: 0,
      next: null,
      page: 0,
      selected: 0
    };

    this.positions = {
      elements: [],
      pages: []
    };

    // Cette variable est "temporaire" ;
    // elle se RESET quand le scroll est OK.
    // Elle permet de bloquer certaines
    // actions lors du scroll manuel.
    this.valueScrollLeft = null;

    this.init();
  }

  /*
   *
   *   INIT
   *
   */

  init() {
    this.initPositions();
    this.initEventListeners();
    if (this.options.navButtons) this.buildButtons();
    if (this.options.slideControls) this.buildSlideControls();

    this.classList.add('--js');
    this.els.scroller.setAttribute('tabindex', '-1');

    if (this.options.autoPlay) this.buildProgressBar();

    // Defer DOM reads to avoid forced reflow after DOM modifications
    requestAnimationFrame(() => {
      this.update();
      this.hideArrows();
    });
  }

  initEventListeners() {
    window.addEventListener('resize', new DebounceEventListener(this.resize.bind(this), 100));
    if (this.options.navButtons) {
      window.addEventListener('resize', new DebounceEventListener(this.hideArrows.bind(this), 250));
    }
    this.addEventListener('keydown', new ThrottleEventListener(this.keyDown.bind(this), 100));
    this.els.scroller.addEventListener('scroll', new ThrottleEventListener(this.update.bind(this), 50));
    this.els.scroller.addEventListener(
      'scroll',
      new DebounceEventListener(() => {
        this.valueScrollLeft = null;
        this.update();
      }, 250)
    );

    if (this.options.observeSizeChangeItems) {
      const observer = new ResizeObserver(() => {
        this.els.scroller.dispatchEvent(new CustomEvent('scrollResize'));
      });
      for (const el of this.els.items) {
        observer.observe(el);
      }
      this.els.scroller.addEventListener('scrollResize', new DebounceEventListener(this.resize.bind(this), 100));
    }

    // Si le carrousel est invisible au moment de l'init, on attend qu'il soit visible pour faire l'update
    if (this.offsetWidth <= 0) {
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);

            requestAnimationFrame(() => {
              this.update();
              this.hideArrows();
            });
          }
        }
      });
      observer.observe(this);
    }
  }

  initPositions() {
    const elements = [],
      pages = [];
    let n = 0;

    // Utiliser getBoundingClientRect pour des valeurs précises avec la mise à l'échelle
    const scrollerRect = this.els.scroller.getBoundingClientRect();

    if (this.els.items[0] && this.els.items[0].offsetWidth <= this.offsetWidth) {
      pages.push(0);
    }

    for (const item of this.els.items) {
      const itemMarginLeft = getComputedStyle(item).marginLeft;

      // Calculer la position avec arrondi pour éviter les erreurs de sous-pixels
      const startX = Math.round(item.offsetLeft);
      const endX = Math.round(item.offsetLeft + item.offsetWidth);

      /* ELEMENTS */
      elements.push({
        startX: startX,
        endX: endX
      });

      /* PAGES */
      // Tolérance de 1px pour les erreurs d'arrondi dues à la mise à l'échelle
      if (endX - n > scrollerRect.width + 1) {
        pages.push(startX - (Number.isNaN(Number.parseFloat(itemMarginLeft)) ? 0 : Number.parseFloat(itemMarginLeft)));
        n = startX;
      }
    }

    this.positions = { elements, pages };
  }

  // Options for smile strate player, work with navButtons
  setColorArrow(index) {
    const item = this.els.items[index];
    const element = item ? item.querySelector('.f-player-blocWrapper') || item.firstElementChild : null;
    if (!element) return;

    const itemStyle = getComputedStyle(element);
    if (itemStyle == null) return;

    const propertyColor = '--carousel-arrow-color';
    const propertyBgColor = '--carousel-arrow-bgcolor';
    const color = itemStyle.getPropertyValue(propertyColor) || null;
    const bgColor = itemStyle.getPropertyValue(propertyBgColor) || null;
    const setOrRemoveProperty = (propertyName, value) => {
      for (const icon of [this.els.buttonPrev, this.els.buttonNext]) {
        if (!icon) continue;
        if (value == null) {
          icon.style.removeProperty(propertyName);
        } else {
          icon.style.setProperty(propertyName, value);
        }
      }
    };
    setOrRemoveProperty(propertyColor, color);
    setOrRemoveProperty(propertyBgColor, bgColor);
  }

  buildButtons() {
    this.els.buttonPrev = buildElement('button', {
      attributes: {
        type: 'button',
        class: 'f-carousel__arrow f-carousel__arrow--prev',
        title: 'Page précédente'
      },
      prependChild: this,
      eventListener: [{ click: this.previousPage.bind(this) }]
    });

    this.els.buttonNext = buildElement('button', {
      attributes: {
        type: 'button',
        class: 'f-carousel__arrow f-carousel__arrow--next',
        title: 'Page suivante'
      },
      prependChild: this,
      eventListener: [{ click: this.nextPage.bind(this) }]
    });
    if (this.options.customizeArrow) this.setColorArrow(this.index.selected);
  }

  buildSlideControls() {
    if (this.els.slideControls) {
      this.els.slideControls.innerHTML = '';
    } else {
      this.els.slideControls = buildElement('div', {
        attributes: {
          role: 'tablist',
          class: 'f-carousel__slideControls',
          'aria-label': 'Contrôles des pages du carrousel',
          tabindex: '0'
        },
        prependChild: this
      });
    }

    for (let index = 0; index < this.positions.pages.length; index++) {
      buildElement('button', {
        attributes: {
          type: 'button',
          class: 'f-carousel__buttonControl',
          title: `Afficher la page ${index + 1} sur ${this.positions.pages.length} du carrousel`,
          role: 'tab',
          'aria-selected': index === this.index.page ? 'true' : 'false',
          tabindex: index === this.index.page ? '-1' : '0'
        },
        appendChild: this.els.slideControls,
        eventListener: [{ click: () => this.goToPage(index) }]
      });
    }
  }

  buildProgressBar() {
    this.els.progress = buildElement('div', {
      attributes: {
        class: 'f-carousel__progress'
      },
      appendChild: this,
      contentHTML: `<div class='f-carousel__progressContent' style='--duration: ${this.options.autoPlaySpeed}s;'></div>`,
      eventListener: [{ animationiteration: () => this.nextPage() }]
    });

    this.start();
  }

  /*
   *
   *   RESIZE
   *
   */

  resize() {
    if (this.offsetWidth <= 0) return;

    this.initPositions();

    if (this.options.slideControls) this.buildSlideControls();
    // Defer update to avoid forced reflow after DOM modifications
    requestAnimationFrame(() => this.update());
  }

  /*
   *
   *   UPDATE (OnScroll ou init)
   *
   */

  update() {
    /*
     *   INDEX UPDATE
     */

    // Tolérance pour les erreurs d'arrondi dues à la mise à l'échelle
    const tolerance = 2;
    const scrollLeft = Math.round(this.els.scroller.scrollLeft);
    const targetScrollLeft = this.valueScrollLeft === null ? scrollLeft : Math.round(this.valueScrollLeft);

    this.index.first = this.positions.elements.findIndex((element) => element.endX > scrollLeft + tolerance);
    this.index.next = findLastIndexCompat(
      this.positions.elements,
      (element) => element.startX < scrollLeft + this.els.scroller.clientWidth - tolerance
    );

    // Trouver la dernière page dont la position est <= au scroll actuel (pas la première >= )
    this.index.page = findLastIndexCompat(this.positions.pages, (pageX) => pageX <= targetScrollLeft + tolerance);
    // Si aucune page trouvée, on est à la première page
    if (this.index.page === -1) {
      this.index.page = 0;
    }

    if (this.valueScrollLeft === null) {
      const nextIndexSelected = Math.min(this.index.selected, this.index.next);

      this.index.selected = this.index.selected <= this.index.first ? this.index.first : nextIndexSelected;
    }

    /* PICKER CONTROLS */
    if (this.els.slideControls) {
      for (let index = 0; index < this.els.slideControls.children.length; index++) {
        if (index === this.index.page) {
          this.els.slideControls.children[index].setAttribute('tabindex', '-1');
          this.els.slideControls.children[index].setAttribute('aria-selected', 'true');
        } else {
          this.els.slideControls.children[index].removeAttribute('tabindex');
          this.els.slideControls.children[index].setAttribute('aria-selected', 'false');
        }
      }
    }

    /* ARIA-LIVE */
    this.els.scroller.setAttribute('aria-live', this.options.autoPlay ? 'off' : 'polite');

    /* NEXT-PREV BUTTONS */
    if (this.options.loop) {
      return; // No need to disable buttons in loop mode, they are always active
    } else {
      this.disableButton(this.els.buttonPrev, this.els.scroller.scrollLeft <= this.options.marginForDisplayNavButton);
      this.disableButton(
        this.els.buttonNext,
        this.els.scroller.scrollLeft + this.els.scroller.offsetWidth >=
          this.els.scroller.scrollWidth - this.options.marginForDisplayNavButton
      );
    }
  }

  disableButton(buttonElement, needDisable) {
    if (!buttonElement) return;
    needDisable ? buttonElement.setAttribute('aria-disabled', 'true') : buttonElement.removeAttribute('aria-disabled');
  }

  hideArrows() {
    if (!this.els.buttonPrev || !this.els.buttonNext) return;
    const prevDisabled = this.els.buttonPrev.hasAttribute('aria-disabled');
    const nextDisabled = this.els.buttonNext.hasAttribute('aria-disabled');
    const hideArrows = prevDisabled && nextDisabled;

    // Ajouter/retirer une classe sur le carrousel
    this.classList.toggle('--hiddenArrows', hideArrows);
  }

  /*
   *
   *    NAVIGATION
   *
   */

  nextElement() {
    this.index.selected = Math.min(++this.index.selected, this.els.items.length - 1);

    if (this.index.selected > this.index.next) {
      this.scrollLeft(this.positions.elements[this.index.selected].endX + 10 - this.els.scroller.clientWidth);

      return;
    }

    this.update();
  }

  previousElement() {
    this.index.selected = Math.max(--this.index.selected, 0);

    if (this.index.selected < this.index.first) {
      this.scrollLeft(this.positions.elements[this.index.selected].startX);

      return;
    }

    this.update();
  }

  nextPage() {
    if (this.positions.pages.length === 0) return;

    const indexPage = this.options.loop
      ? (this.index.page + 1) % this.positions.pages.length
      : Math.min(this.index.page + 1, this.positions.pages.length - 1);

    this.scrollLeft(this.positions.pages[indexPage]);
    if (this.options.customizeArrow) this.setColorArrow(indexPage);
  }

  previousPage() {
    if (this.positions.pages.length === 0) return;

    const indexPage = this.options.loop
      ? (this.index.page - 1 + this.positions.pages.length) % this.positions.pages.length
      : Math.max(this.index.page - 1, 0);

    this.scrollLeft(this.positions.pages[indexPage]);
    if (this.options.customizeArrow) this.setColorArrow(indexPage);
  }

  goToPage(index) {
    if (this.positions.pages.length === 0) return;

    const indexPage = Math.min(Math.max(index, 0), this.positions.pages.length - 1);

    this.scrollLeft(this.positions.pages[indexPage]);
    if (this.options.customizeArrow) this.setColorArrow(indexPage);
  }

  /* KEYBOARD */
  keyDown(event) {
    switch (event.key) {
      case 'ArrowRight':
        if (this.options.autoPlay) this.stop();
        this.nextElement();
        break;
      case 'ArrowLeft':
        if (this.options.autoPlay) this.stop();
        this.previousElement();
        break;
    }
  }

  /* NAVIGATION HELPER */
  scrollLeft(valueScrollLeft) {
    this.valueScrollLeft = valueScrollLeft;
    this.els.scroller.scrollLeft = this.valueScrollLeft;
  }

  /* AUTOPLAY */
  start() {
    this.els.progress.getElementsByClassName('f-carousel__progressContent')[0].style['animation-play-state'] = '';
  }

  stop() {
    this.els.progress.getElementsByClassName('f-carousel__progressContent')[0].style['animation-play-state'] = 'paused';
  }
}

/*
 *
 *   HELPERS
 *
 */

const camelToSnakeCase = (str) => str.replaceAll(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

const mergeOptionsFromAttributes = (element, options) => {
  return Object.fromEntries(
    Object.entries(options).map((value) => {
      const attr = element.getAttribute(camelToSnakeCase(value[0]));
      return [value[0], attr === null ? value[1] : parseAttributeValue(attr)];
    })
  );
};

const parseAttributeValue = (attributeValue) => {
  return /^-?\d+$/.test(attributeValue) ? Number.parseFloat(attributeValue) : parseBooleanOrObject(attributeValue);
};

const parseBooleanOrObject = (value) => {
  if (typeof value == 'boolean' || value === '') {
    return value === 'true' || value === '';
  }

  return value;
};

const buildElement = (tagName, { attributes = {}, contentHTML, appendChild, prependChild, eventListener }) => {
  const element = document.createElement(tagName);
  for (const key of Object.keys(attributes)) {
    element.setAttribute(key, attributes[key]);
  }

  if (contentHTML) element.innerHTML = contentHTML;

  if (prependChild) prependChild.insertBefore(element, prependChild.firstChild);
  else if (appendChild) appendChild.appendChild(element);

  if (eventListener) {
    for (const event of eventListener) {
      for (const key of Object.keys(event)) {
        element.addEventListener(key, event[key]);
      }
    }
  }

  return element;
};

// Polyfill for Array.prototype.findLastIndex
const findLastIndexCompat = (array, predicate) => {
  if (Array.prototype.findLastIndex) {
    return array.findLastIndex(predicate);
  }
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate(array[i], i, array)) return i;
  }
  return -1;
};

customElements.get('f-carousel') || customElements.define('f-carousel', Carousel);
